import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Wrench, ArrowLeft, Mail, Phone, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [siteIdentity, setSiteIdentity] = useState(null);
  const [resetMethods, setResetMethods] = useState({ sms: false, email: false });
  
  const [formData, setFormData] = useState({
    identifier: '',
    method: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    axios.get(`${API}/cms/landing`)
      .then(res => {
        if (res.data?.site_identity) setSiteIdentity(res.data.site_identity);
      })
      .catch(() => {});
    
    axios.get(`${API}/auth/reset-methods`)
      .then(res => setResetMethods(res.data || { sms: false, email: false }))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const noMethodsAvailable = !resetMethods.sms && !resetMethods.email;

  const handleFindAccount = async (e) => {
    e.preventDefault();
    if (!formData.identifier) {
      toast.error('Please enter your email or phone number');
      return;
    }
    if (noMethodsAvailable) {
      toast.error('Password reset is currently unavailable. Please contact support.');
      return;
    }
    
    setIsLoading(true);
    try {
      const method = resetMethods.sms ? 'sms' : 'email';
      await axios.post(`${API}/auth/forgot-password`, {
        identifier: formData.identifier,
        method
      });
      setStep(2);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No account found with this email or phone number');
      } else {
        toast.error(error.response?.data?.detail || 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async (method) => {
    setFormData(prev => ({ ...prev, method }));
    setIsLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        identifier: formData.identifier,
        method
      });
      
      toast.success(res.data.message || 'Reset code sent!');
      if (res.data.debug_otp) {
        toast.info(`Debug OTP: ${res.data.debug_otp}`, { duration: 15000 });
      }
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (formData.otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setStep(4);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        identifier: formData.identifier,
        otp: formData.otp,
        new_password: formData.newPassword
      });
      
      toast.success('Password reset successful!');
      setStep(5);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <header className="p-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => step > 1 && step < 5 ? setStep(step - 1) : navigate('/login')}
          className="text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            {siteIdentity?.site_logo ? (
              <img 
                src={siteIdentity.site_logo.startsWith('http') 
                    ? siteIdentity.site_logo 
                    : `${BACKEND_URL}${siteIdentity.site_logo}`
                } 
                alt={siteIdentity?.site_title || 'HandyBayani'} 
                className="h-16 w-16 object-contain mx-auto mb-4 rounded-2xl"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 p-2">
                <img src="/handybayani-logo.png" alt="HandyBayani" className="w-12 h-12 object-contain" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">
              {step === 5 ? 'Password Reset!' : 'Reset Password'}
            </h1>
            <p className="text-slate-500 mt-1">
              {step === 1 && 'Enter your email or phone number'}
              {step === 2 && 'Choose how to receive your reset code'}
              {step === 3 && 'Enter the code we sent you'}
              {step === 4 && 'Create your new password'}
              {step === 5 && 'Your password has been reset'}
            </p>
          </div>

          {/* Step 1: Enter identifier */}
          {step === 1 && (
            <form onSubmit={handleFindAccount} className="space-y-4">
              {noMethodsAvailable && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="no-methods-warning">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Password reset unavailable</p>
                    <p className="text-xs text-amber-600 mt-0.5">Neither SMS nor Email is configured. Please contact support to reset your password.</p>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="identifier">Email or Phone Number</Label>
                <Input
                  id="identifier"
                  name="identifier"
                  type="text"
                  placeholder="you@example.com or 09171234567"
                  value={formData.identifier}
                  onChange={handleChange}
                  className="h-12 mt-1.5"
                  required
                  disabled={noMethodsAvailable}
                  data-testid="forgot-identifier-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                disabled={isLoading || noMethodsAvailable}
                data-testid="forgot-find-btn"
              >
                {isLoading ? 'Finding account...' : 'Find My Account'}
              </Button>
              
              <p className="text-center text-sm text-slate-500">
                Remember your password?{' '}
                <Link to="/login" className="text-amber-600 hover:underline">Sign in</Link>
              </p>
            </form>
          )}

          {/* Step 2: Choose method */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-center text-slate-600 mb-6">
                We found your account! How would you like to receive your reset code?
              </p>
              
              <Button
                onClick={() => handleSendCode('sms')}
                variant="outline"
                className={`w-full h-14 justify-start px-4 ${!resetMethods.sms ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoading || !resetMethods.sms}
                data-testid="forgot-sms-btn"
              >
                <Phone className="w-5 h-5 mr-3 text-amber-600" />
                <div className="text-left">
                  <p className="font-medium">Send via SMS</p>
                  <p className="text-xs text-slate-500">
                    {resetMethods.sms ? "We'll text you a code" : 'SMS is not available'}
                  </p>
                </div>
              </Button>
              
              <Button
                onClick={() => handleSendCode('email')}
                variant="outline"
                className={`w-full h-14 justify-start px-4 ${!resetMethods.email ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoading || !resetMethods.email}
                data-testid="forgot-email-btn"
              >
                <Mail className="w-5 h-5 mr-3 text-amber-600" />
                <div className="text-left">
                  <p className="font-medium">Send via Email</p>
                  <p className="text-xs text-slate-500">
                    {resetMethods.email ? "We'll email you a code" : 'Email is not available'}
                  </p>
                </div>
              </Button>

              {!resetMethods.sms && !resetMethods.email && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">No reset methods are available. Please contact support.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Enter OTP */}
          {step === 3 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <Label htmlFor="otp">Reset Code</Label>
                <Input
                  id="otp"
                  name="otp"
                  type="text"
                  placeholder="123456"
                  value={formData.otp}
                  onChange={handleChange}
                  className="h-14 mt-1.5 text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                  data-testid="forgot-otp-input"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Didn't receive?{' '}
                  <button 
                    type="button" 
                    onClick={() => handleSendCode(formData.method)}
                    className="text-amber-600 hover:underline"
                    disabled={isLoading}
                  >
                    Resend code
                  </button>
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                disabled={isLoading || formData.otp.length !== 6}
                data-testid="forgot-verify-btn"
              >
                Verify Code
              </Button>
            </form>
          )}

          {/* Step 4: New password */}
          {step === 4 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="h-12 pr-12"
                    required
                    minLength={6}
                    data-testid="forgot-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="h-12 mt-1.5"
                  required
                  data-testid="forgot-confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                disabled={isLoading}
                data-testid="forgot-reset-btn"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {/* Step 5: Success */}
          {step === 5 && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-slate-600">
                Your password has been reset successfully. Redirecting to login...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
